import { createContext, useCallback, useContext, useState } from "react";
import type { ReactNode } from "react";

export interface UpgradePlanHeaderConfig {
  title: string;
}

export interface UpgradePlanModalSecondaryAction {
  "data-testid"?: string;
  label?: string;
  onClick?: () => void;
  shouldCloseModal?: boolean;
}

export interface UpgradePlanModalPrimaryAction {
  "data-testid"?: string;
  disabled?: boolean;
  form?: string;
  label: string;
  loading?: boolean;
  onClick?: () => void;
  shouldCloseModal?: boolean;
  type?: "button" | "submit" | "reset";
  variant?: "primary" | "destructive";
}

export interface UpgradePlanFooterConfig {
  children?: ReactNode;
  className?: string;
  layout?: "default" | "full-width";
  primaryAction?: UpgradePlanModalPrimaryAction;
  secondaryAction?: UpgradePlanModalSecondaryAction;
  startContent?: ReactNode;
}

interface UpgradePlanModalConfigSetters {
  setFooterConfig: (config: UpgradePlanFooterConfig | null) => void;
  setHeaderConfig: (config: UpgradePlanHeaderConfig | null) => void;
}

const SettersContext = createContext<UpgradePlanModalConfigSetters | null>(
  null,
);
SettersContext.displayName = "UpgradePlanModalSettersContext";

export const UpgradePlanModalSettersProvider = ({
  children,
  setFooterConfig,
  setHeaderConfig,
}: UpgradePlanModalConfigSetters & { children: ReactNode }) => {
  const value = { setFooterConfig, setHeaderConfig };

  return (
    <SettersContext.Provider value={value}>{children}</SettersContext.Provider>
  );
};

export const useIsCompassModal = () => {
  const ctx = useContext(SettersContext);
  return ctx !== null;
};

export const useSetUpgradePlanModalFooter = () => {
  const ctx = useContext(SettersContext);
  return ctx?.setFooterConfig ?? null;
};

export const useSetUpgradePlanModalHeader = () => {
  const ctx = useContext(SettersContext);
  return ctx?.setHeaderConfig ?? null;
};

export const useUpgradePlanModalConfig = () => {
  const [footerConfig, setFooterConfigState] =
    useState<UpgradePlanFooterConfig | null>(null);
  const [headerConfig, setHeaderConfigState] =
    useState<UpgradePlanHeaderConfig | null>(null);

  const setFooterConfig = useCallback(
    (config: UpgradePlanFooterConfig | null) => {
      setFooterConfigState(config);
    },
    [],
  );

  const setHeaderConfig = useCallback(
    (config: UpgradePlanHeaderConfig | null) => {
      setHeaderConfigState(config);
    },
    [],
  );

  const shouldShowFooter = Boolean(
    footerConfig?.primaryAction ||
    footerConfig?.secondaryAction ||
    footerConfig?.children ||
    footerConfig?.startContent,
  );

  return {
    footerConfig,
    headerConfig,
    setFooterConfig,
    setHeaderConfig,
    shouldShowFooter,
  };
};
